import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { getApiClient } from '../../api/apiClient';
import { 
  XMarkIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
  IdentificationIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserPlusIcon,
  CogIcon,
  ClockIcon,
  PhotoIcon,
  PencilIcon,
  UserGroupIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import AddShopAccountModal from './AddShopAccountModal.jsx';
import EditShopConfigModal from './EditShopConfigModal.jsx';
import EmployeeManagementModal from './EmployeeManagementModal.jsx';
import EditPublicProfileModal from './EditPublicProfileModal.jsx';

const AutoShopDetailsModal = ({ shop, isOpen, onClose, onShopUpdate }) => {
  const { t } = useLanguage();
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showEditConfigModal, setShowEditConfigModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showPublicProfileModal, setShowPublicProfileModal] = useState(false);
  const [shopConfig, setShopConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);

  // Fetch shop configuration
  const fetchShopConfig = async (shopId) => {
    if (!shopId) return;
    
    setConfigLoading(true);
    setConfigError(null);
    
    try {
      const client = getApiClient();
      const response = await client.get(`/admin/shop/get-config/${shopId}`);
      
      setShopConfig(response.data);
    } catch (error) {
      console.error('Error fetching shop configuration:', error);
      if (error.response?.status === 404) {
        setConfigError('Configuration not found');
      } else if (error.response?.status === 403) {
        setConfigError('Insufficient permissions');
      } else {
        setConfigError('Failed to load configuration');
      }
    } finally {
      setConfigLoading(false);
    }
  };

  // Fetch config when modal opens and shop changes
  useEffect(() => {
    if (isOpen && shop?.id) {
      fetchShopConfig(shop.id);
    } else {
      setShopConfig(null);
      setConfigError(null);
    }
  }, [isOpen, shop?.id]);

  if (!isOpen || !shop) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-11/12 md:w-4/5 lg:w-3/4 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <BuildingStorefrontIcon className="h-8 w-8 text-purple-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-900">
              Auto Shop Details
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BuildingStorefrontIcon className="h-5 w-5 mr-2 text-purple-600" />
              Basic Information
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Shop ID:</label>
                <p className="text-sm text-gray-900 font-mono">{shop.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Brand Name:</label>
                <p className="text-sm text-gray-900">{shop.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Business Name:</label>
                <p className="text-sm text-gray-900">{shop.company || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Email:</label>
                <p className="text-sm text-gray-900">{shop.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Phone:</label>
                <p className="text-sm text-gray-900">{shop.phone}</p>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2 text-green-600" />
              Location Information
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Address:</label>
                <p className="text-sm text-gray-900">{shop.address || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">City:</label>
                <p className="text-sm text-gray-900">{shop.city || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Country:</label>
                <p className="text-sm text-gray-900">{shop.countryCode || 'RO'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Subdivision:</label>
                <p className="text-sm text-gray-900">{shop.subdivisionCode || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <IdentificationIcon className="h-5 w-5 mr-2 text-blue-600" />
              Business Information
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Tax Identifier:</label>
                <p className="text-sm text-gray-900">{shop.taxIdentifier || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Registration Number:</label>
                <p className="text-sm text-gray-900">{shop.regCom || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">VAT Payer:</label>
                <div className="flex items-center">
                  {shop.vatPayer ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className="text-sm text-gray-900">
                    {shop.vatPayer ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Bank Name:</label>
                <p className="text-sm text-gray-900">{shop.bankName || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">IBAN:</label>
                <p className="text-sm text-gray-900 font-mono">{shop.iban || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Account & Permissions */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <ShieldCheckIcon className="h-5 w-5 mr-2 text-orange-600" />
              Account & Permissions
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Firebase UID:</label>
                {shop.firebaseUid ? (
                  <p className="text-sm text-gray-900 font-mono">{shop.firebaseUid}</p>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">N/A</p>
                    <button
                      onClick={() => setShowAddAccountModal(true)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                    >
                      <UserPlusIcon className="h-4 w-4 mr-1" />
                      {t('addShopAccount')}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Role:</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  shop.role === 'AUTO_SHOP' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {shop.role || 'Not Set'}
                </span>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Package:</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  shop.permissions === 'PACKAGE_1' 
                    ? 'bg-purple-100 text-purple-800' 
                    : shop.permissions === 'PACKAGE_2'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {shop.permissions || 'No Package'}
                </span>
              </div>
            </div>
          </div>

          {/* MFinante Integration */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BanknotesIcon className="h-5 w-5 mr-2 text-red-600" />
              MFinante Integration
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Has Tokens:</label>
                <div className="flex items-center">
                  {shop.hasMfinanteTokens ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className="text-sm text-gray-900">
                    {shop.hasMfinanteTokens ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Tokens Valid:</label>
                <div className="flex items-center">
                  {shop.tokensValid ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className="text-sm text-gray-900">
                    {shop.tokensValid ? 'Valid' : 'Invalid'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Tokens Expire At:</label>
                <p className="text-sm text-gray-900">
                  {formatDate(shop.tokensExpireAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Autopass Integration */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2 text-indigo-600" />
              Autopass Integration
            </h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Has Credentials:</label>
                <div className="flex items-center">
                  {shop.hasAutopassCredentials ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className="text-sm text-gray-900">
                    {shop.hasAutopassCredentials ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Token Valid:</label>
                <div className="flex items-center">
                  {shop.autopassTokenValid ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className="text-sm text-gray-900">
                    {shop.autopassTokenValid ? 'Valid' : 'Invalid'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Autopass Email:</label>
                <p className="text-sm text-gray-900">{shop.autopassEmail || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">User ID:</label>
                <p className="text-sm text-gray-900">{shop.autopassUserId || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Shop Configuration */}
          <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <CogIcon className="h-5 w-5 mr-2 text-green-600" />
                {t('shopConfiguration')}
              </h4>
              {shopConfig && !configLoading && (
                <button
                  onClick={() => setShowEditConfigModal(true)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  {t('editConfiguration')}
                </button>
              )}
            </div>
            
            {configLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-2 text-sm text-gray-600">{t('loadingConfiguration')}</span>
              </div>
            ) : configError ? (
              <div className="flex items-center py-4">
                <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-sm text-red-600">{configError}</span>
              </div>
            ) : shopConfig ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Working Hours */}
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('workingHours')}:</label>
                  <div className="flex items-center mt-1">
                    <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-sm text-gray-900">
                      {shopConfig.startTime} - {shopConfig.endTime}
                    </span>
                  </div>
                </div>

                {/* Online Appointments */}
                <div>
                  <label className="text-sm font-medium text-gray-600">{t('onlineAppointments')}:</label>
                  <div className="flex items-center mt-1">
                    {shopConfig.onlineAppointments ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                    )}
                    <span className="text-sm text-gray-900">
                      {shopConfig.onlineAppointments ? t('enabled') : t('disabled')}
                    </span>
                  </div>
                </div>

                {/* Profile Picture */}
                {shopConfig.profilePic && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">{t('profilePicture')}:</label>
                    <div className="flex items-center mt-1">
                      <PhotoIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <a 
                        href={shopConfig.profilePic} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-purple-600 hover:text-purple-800 underline"
                      >
                        {t('viewImage')}
                      </a>
                    </div>
                  </div>
                )}

                {/* Description */}
                {shopConfig.description && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-600">{t('description')}:</label>
                    <p className="text-sm text-gray-900 mt-1 p-2 bg-white rounded border">
                      {shopConfig.description}
                    </p>
                  </div>
                )}

                {/* Working Hours Detail
                {shopConfig.workingHours && shopConfig.workingHours.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-600">{t('detailedWorkingHours')}:</label>
                    <div className="mt-1 space-y-1">
                      {shopConfig.workingHours.map((daySchedule, index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-white rounded border text-sm">
                          <span className="font-medium text-gray-700">{daySchedule.day || `Day ${index + 1}`}:</span>
                          <span className="text-gray-900">
                            {daySchedule.start && daySchedule.end 
                              ? `${daySchedule.start} - ${daySchedule.end}`
                              : daySchedule.closed 
                                ? t('closed') 
                                : 'N/A'
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )} */}
              </div>
            ) : (
              <div className="flex items-center py-4">
                <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-500">{t('noConfigurationAvailable')}</span>
              </div>
            )}
          </div>

          {/* Public Profile */}
          <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
            <div className="flex justify-between items-center">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <GlobeAltIcon className="h-5 w-5 mr-2 text-blue-600" />
                {t('publicProfile')}
              </h4>
              <button
                onClick={() => setShowPublicProfileModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                {t('editPublicProfile')}
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {t('managePublicShopProfile')}
            </p>
          </div>

          {/* Employee Management */}
          <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
            <div className="flex justify-between items-center">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <UserGroupIcon className="h-5 w-5 mr-2 text-indigo-600" />
                {t('employeeManagement')}
              </h4>
              <button
                onClick={() => setShowEmployeeModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <UserGroupIcon className="h-4 w-4 mr-2" />
                {t('manageEmployees')}
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {t('viewAddAndManageEmployees')}
            </p>
          </div>

          {/* Timestamps */}
          <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2 text-gray-600" />
              Timestamps
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Created At:</label>
                <p className="text-sm text-gray-900">{formatDate(shop.createdAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Updated At:</label>
                <p className="text-sm text-gray-900">{formatDate(shop.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Close
          </button>
        </div>
      </div>

      {/* Add Shop Account Modal */}
      <AddShopAccountModal 
        isOpen={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        onSuccess={() => {
          setShowAddAccountModal(false);
          if (onShopUpdate) onShopUpdate();
        }}
        shop={shop}
      />

      {/* Edit Shop Configuration Modal */}
      <EditShopConfigModal 
        isOpen={showEditConfigModal}
        onClose={() => setShowEditConfigModal(false)}
        onSuccess={() => {
          setShowEditConfigModal(false);
          fetchShopConfig(shop?.id); // Refresh configuration
        }}
        shop={shop}
        currentConfig={shopConfig}
      />

      {/* Employee Management Modal */}
      <EmployeeManagementModal 
        isOpen={showEmployeeModal}
        onClose={() => setShowEmployeeModal(false)}
        shop={shop}
      />

      {/* Public Profile Modal */}
      <EditPublicProfileModal 
        isOpen={showPublicProfileModal}
        onClose={() => setShowPublicProfileModal(false)}
        shop={shop}
        onSuccess={() => {
          setShowPublicProfileModal(false);
          // Could refresh public profile data here if we were displaying it
        }}
      />
    </div>
  );
};

export default AutoShopDetailsModal;
